import type {
  ICampaign,
  ICampaignSummary,
  ICreateApplication,
  IGigInitialState,
  INavigationHistory,
  IThunkAPI,
} from '@/store/types';
import { gigApis } from '@/api/gig';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

export const initialState: IGigInitialState = {
  liveCampaigns: [],
  selectedCampaignId: 0,
  isLoadingGigDetail: false,
  campaignDetail: {
    campaignId: 0,
    startDate: '',
    endDate: '',
    gigId: 0,
    description: '',
    headcountMax: 0,
    statusId: 0,
    title: '',
    gigCountries: [],
    gigLanguages: [],
    gigHardwareRequirement: {
      hardwareRequirementCategories: [],
    },
    vocabCampaignStatus: {
      campaignStatusId: 0,
      label: '',
      listOrder: 0,
      visibility: 0,
    },
    gig: {
      backgroundCheckRequired: false,
      gigId: 0,
      gigTypeId: 0,
      gigLengthId: 0,
      clientId: 0,
      title: '',
      description: '',
      operatingHours: '',
      vocabGigType: {
        gigTypeId: 0,
        label: '',
        description: null,
        listOrder: 0,
        visibility: false,
        gigId: null,
      },
      networkProfile: {
        client: null,
        clientId: 0,
        downloadCos: 0,
        downloadSpeed: 0,
        label: '',
        networkProfileId: 0,
        uploadCos: 0,
        uploadSpeed: 0,
      },
      vocabGigLength: {
        id: 0,
        label: '',
        description: '',
        listorder: 0,
        visibility: false,
      },
      course: {
        description: '',
        duration: 0,
        title: '',
        videoLabel: '',
        videoLink: '',
        deferredPaymentAllowed: false,
        cost: {
          cost: 0,
          isoCode: '',
          labelLong: '',
          labelShort: '',
        },
        vocabCourseDurationType: {
          label: '',
        },
      },
      client: {
        clientName: '',
      },
      mandatoryRequirements: '',
      conflictOfInterest: '',
      experience: '',
    },
  },
  comingSoonGigs: [],
  appliedGigs: [],
  contractRequirements: [],
  selectedApplicationId: 0,
  selectedGigId: 0,
};

// list gigs
export const getLiveCampaignDetail = createAsyncThunk<ICampaignSummary, { campaignId: number }, IThunkAPI>(
  'campaign/detail/live',
  async (requestObject, thunkAPI) => {
    try {
      const response = await gigApis.getLiveCampaignDetail(requestObject.campaignId);

      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getLiveCampaignsSummary = createAsyncThunk<ICampaign[], undefined, IThunkAPI>(
  'campaigns/summary/live',
  async (_, thunkAPI) => {
    try {
      const response = await gigApis.getLiveCampaignsSummary();
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getComingSoonGigs = createAsyncThunk<unknown, undefined, IThunkAPI>(
  'campaign/list/wait',
  async (_, thunkAPI) => {
    try {
      const response = await gigApis.getComingSoonGigs();

      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getAppliedGigsList = createAsyncThunk<unknown, undefined, IThunkAPI>(
  'application/list',
  async (_, thunkAPI) => {
    try {
      const response = await gigApis.getAppliedGigs();

      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);
export const getContractRequirements = createAsyncThunk<unknown, { gigId: number; countryId: number }, IThunkAPI>(
  'campaign/contract/list',
  async (reqObj, thunkAPI) => {
    try {
      const { gigId, countryId } = reqObj;
      const response = await gigApis.getContractRequirements(gigId, countryId);

      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const createGigApplication = createAsyncThunk<any, ICreateApplication, IThunkAPI>(
  'application/create',
  async (payload, thunkAPI) => {
    try {
      const response = await gigApis.createApplication(payload);

      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const createGigNavigationHistory = createAsyncThunk<unknown, INavigationHistory, IThunkAPI>(
  'application/create',
  async (payload, thunkAPI) => {
    try {
      const response = await gigApis.createNavigationHistory(payload);

      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const gigSlice = createSlice({
  name: 'gigSlice',
  initialState,
  reducers: {
    setApplicationId(state, action) {
      state.selectedApplicationId = action.payload;
    },
    setGigId(state, action) {
      state.selectedGigId = action.payload;
    },
    setCampignId(state, action) {
      state.selectedCampaignId = action.payload;
    },
  },
  extraReducers: {
    'campaigns/summary/live/fulfilled': (state, action) => {
      state.liveCampaigns = action.payload.result;
    },
    'campaign/detail/live/pending': state => {
      state.isLoadingGigDetail = true;
    },
    'campaign/detail/live/rejected': (state, action) => {
      state.campaignDetail = action.payload.result;
      state.isLoadingGigDetail = false;
    },
    'campaign/detail/live/fulfilled': (state, action) => {
      state.campaignDetail = action.payload.result;
      state.isLoadingGigDetail = false;
    },
    'campaign/list/wait/fulfilled': (state, action) => {
      state.comingSoonGigs = action.payload.result;
    },
    'campaign/contract/list/fulfilled': (state, action) => {
      state.contractRequirements = action.payload;
    },
    'application/list/fulfilled': (state, action) => {
      state.appliedGigs = action.payload.result;
    },
    'application/create/fulfilled': (state, action) => {
      state.selectedApplicationId = action.payload.result;
    },
  },
});

export const { setApplicationId, setGigId, setCampignId } = gigSlice.actions;
export default gigSlice.reducer;
